import { Injectable } from '@angular/core';
import { concat, Observable, of } from 'rxjs';
import { distinctUntilChanged, first, map, timeout } from 'rxjs/operators';

import { AnalyticsService } from './analytics.service';

/**
 * Feature flag de PostHog que habilita el módulo de Cotizaciones (listado, detalle, configuración y
 * "Descargar y Guardar" en el carrito). Hay que crearlo en los DOS proyectos de PostHog (staging y
 * producción); si no existe, `getExperimentVariant` devuelve false y el módulo queda oculto
 * (fail-closed). Con el flag apagado el carrito conserva "Descargar cotización" sin guardar, como
 * antes del módulo.
 *
 * Solo de frontend, igual que `bank-apis`: el API no tiene SDK de PostHog, y los
 * endpoints `/api/price-templates` siguen protegidos por permisos.
 */
export const PRICE_TEMPLATES_FLAG = 'cotizacionesv2';

/** Tope de espera del guard de ruta hasta que carguen los flags de PostHog (~1.5 s). */
const FLAG_WAIT_MS = 3000;

/**
 * Acceso al módulo de Cotizaciones. Vive en `app-common` porque lo consume el side-nav, que no puede
 * importar del módulo lazy del POS.
 */
@Injectable({ providedIn: 'root' })
export class PriceTemplatesAccessService {
    constructor(private readonly analyticsService: AnalyticsService) {}

    /** Lectura síncrona: para `*ngIf`, el menú y las defensas de handler. */
    canAccessSync(): boolean {
        return this.analyticsService.getExperimentVariant(PRICE_TEMPLATES_FLAG);
    }

    /** Valor actual del flag y cada cambio posterior (PostHog los entrega hasta ~1.5 s después del init). */
    canAccessChanges$(): Observable<boolean> {
        return concat(of(null), this.analyticsService.flagsUpdated$).pipe(
            map(() => this.canAccessSync()),
            distinctUntilChanged(),
        );
    }

    /**
     * Para el guard de ruta: `true` apenas el flag se encienda; si no se enciende en `FLAG_WAIT_MS`,
     * el valor actual (fail-closed). Así un F5 no expulsa al usuario mientras carga PostHog.
     */
    canAccess$(): Observable<boolean> {
        return this.canAccessChanges$().pipe(first(Boolean), timeout({ first: FLAG_WAIT_MS, with: () => of(this.canAccessSync()) }));
    }
}
